import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Check, ChevronRight } from "lucide-react";
import "./ContextMenu.css";

/**
 * Windows 11 context menus, shared by every surface.
 *
 * Usage:  const { showMenu } = useMenu();
 *         onContextMenu={(e) => showMenu(e, items)}
 *
 * An item is one of:
 *   { type: "separator" }
 *   { type: "icons", items: [{ icon, label, onClick, disabled }] }   // Win11's
 *                                                                    // top row
 *   { label, icon, shortcut, checked, disabled, danger, onClick }
 *   { label, icon, items: [...] }                                    // submenu
 *
 * Menus render in a fixed layer at the shell root so they are never clipped by
 * a window's overflow.
 */

const MenuContext = createContext(null);

export const useMenu = () => {
  const ctx = useContext(MenuContext);
  if (!ctx) throw new Error("useMenu must be used inside <MenuProvider>");
  return ctx;
};

const isActionable = (item) =>
  item && !item.type && !item.disabled;


/** One row; owns its own rect so a submenu can anchor to it. */
function MenuRow({
  item,
  depth,
  active,
  subOpen,
  onHover,
  onOpenSub,
  onPick,
  onClose,
}) {
  const rowRef = useRef(null);
  const hasSub = Boolean(item.items?.length);
  const Icon = item.icon;

  return (
    <div className="ctx-row" ref={rowRef} onMouseEnter={onHover}>
      <button
        className={[
          "ctx-item",
          item.disabled ? "ctx-item--disabled" : "",
          item.danger ? "ctx-item--danger" : "",
          active ? "ctx-item--cursor" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        role="menuitem"
        disabled={item.disabled}
        onClick={() => (hasSub ? onOpenSub() : onPick())}
      >
        <span className="ctx-item__glyph">
          {item.checked ? <Check size={14} /> : Icon ? <Icon size={15} /> : null}
        </span>
        <span className="ctx-item__label">{item.label}</span>
        {item.shortcut && (
          <span className="ctx-item__shortcut">{item.shortcut}</span>
        )}
        {hasSub && <ChevronRight size={14} className="ctx-item__more" />}
      </button>

      {hasSub && subOpen && (
        <MenuPanel
          items={item.items}
          depth={depth + 1}
          x={0}
          y={0}
          onClose={onClose}
        />
      )}
    </div>
  );
}

function MenuPanel({ items, x, y, onClose, depth = 0 }) {
  const ref = useRef(null);
  const isSub = depth > 0;
  const [pos, setPos] = useState({ left: x, top: y, ready: isSub });
  const [flip, setFlip] = useState({ x: false, y: false });
  const [openSub, setOpenSub] = useState(null);
  const [cursor, setCursor] = useState(-1);
  const subTimer = useRef(null);

  /**
   * Root menus are placed at the pointer in viewport coordinates. Submenus are
   * anchored to their row in CSS — measuring the row from JS races the layout
   * of the menu that contains it — and only flip when they would overflow.
   */
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    if (isSub) {
      const r = el.getBoundingClientRect();
      const pad = 8;
      setFlip({
        x: r.right > window.innerWidth - pad,
        y: r.bottom > window.innerHeight - pad,
      });
      return;
    }

    const { offsetWidth: w, offsetHeight: h } = el;
    const pad = 8;
    let left = x;
    let top = y;
    if (left + w > window.innerWidth - pad) left = window.innerWidth - w - pad;
    if (top + h > window.innerHeight - pad) top = y - h;
    left = Math.max(pad, left);
    top = Math.max(pad, Math.min(top, window.innerHeight - h - pad));
    setPos((prev) =>
      prev.ready && prev.left === left && prev.top === top
        ? prev
        : { left, top, ready: true }
    );
  }, [x, y, isSub]);

  useEffect(() => () => clearTimeout(subTimer.current), []);

  const actionables = useMemo(
    () => items.map((it, i) => (isActionable(it) ? i : -1)).filter((i) => i >= 0),
    [items]
  );

  const move = (delta) => {
    if (!actionables.length) return;
    const at = actionables.indexOf(cursor);
    const next =
      at === -1
        ? actionables[delta > 0 ? 0 : actionables.length - 1]
        : actionables[(at + delta + actionables.length) % actionables.length];
    setCursor(next);
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      move(1);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      move(-1);
    } else if (e.key === "Enter" || e.key === " ") {
      const item = items[cursor];
      if (!item) return;
      e.preventDefault();
      if (item.items) setOpenSub(cursor);
      else {
        item.onClick?.();
        onClose();
      }
    } else if (e.key === "ArrowRight" && items[cursor]?.items) {
      e.preventDefault();
      setOpenSub(cursor);
    } else if (e.key === "ArrowLeft" && depth > 0) {
      e.preventDefault();
      onClose();
    }
  };

  const pick = (item) => {
    if (item.disabled) return;
    item.onClick?.();
    onClose();
  };

  return (
    <div
      ref={ref}
      className={[
        "ctx-menu",
        isSub ? "ctx-menu--sub" : "",
        isSub && flip.x ? "ctx-menu--sub-left" : "",
        isSub && flip.y ? "ctx-menu--sub-up" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      style={
        isSub
          ? undefined
          : {
              left: pos.left,
              top: pos.top,
              visibility: pos.ready ? "visible" : "hidden",
            }
      }
      role="menu"
      tabIndex={-1}
      onKeyDown={onKeyDown}
      onContextMenu={(e) => e.preventDefault()}
    >
      {items.map((item, i) => {
        if (item.type === "separator") {
          return <div className="ctx-sep" key={`sep-${i}`} />;
        }

        if (item.type === "icons") {
          return (
            <div className="ctx-iconrow" key={`icons-${i}`}>
              {item.items.map((tool) => (
                <button
                  key={tool.label}
                  className="ctx-tool"
                  title={tool.label}
                  aria-label={tool.label}
                  disabled={tool.disabled}
                  onClick={() => pick(tool)}
                >
                  <tool.icon size={16} />
                </button>
              ))}
            </div>
          );
        }

        return (
          <MenuRow
            key={item.label}
            item={item}
            index={i}
            depth={depth}
            active={cursor === i}
            subOpen={openSub === i}
            onHover={() => {
              setCursor(i);
              clearTimeout(subTimer.current);
              if (item.items?.length) {
                subTimer.current = setTimeout(() => setOpenSub(i), 160);
              } else {
                setOpenSub(null);
              }
            }}
            onOpenSub={() => setOpenSub(i)}
            onPick={() => pick(item)}
            onClose={onClose}
          />
        );
      })}
    </div>
  );
}

export function MenuProvider({ children }) {
  const [menu, setMenu] = useState(null);

  const close = useCallback(() => setMenu(null), []);

  const showMenu = useCallback((e, items) => {
    if (!items?.length) return;
    e.preventDefault();
    e.stopPropagation();
    setMenu({ x: e.clientX, y: e.clientY, items, key: Date.now() });
  }, []);

  useEffect(() => {
    if (!menu) return;
    const onDown = (e) => {
      if (!e.target.closest(".ctx-menu")) close();
    };
    const onKey = (e) => e.key === "Escape" && close();
    const onBlur = () => close();
    window.addEventListener("pointerdown", onDown, true);
    window.addEventListener("keydown", onKey);
    window.addEventListener("blur", onBlur);
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("pointerdown", onDown, true);
      window.removeEventListener("keydown", onKey);

    };
  }, [menu, close]);

  const value = useMemo(() => ({ showMenu, closeMenu: close }), [showMenu, close]);

  return (
    <MenuContext.Provider value={value}>
      {children}
      {menu && (
        <div className="ctx-layer">
          <MenuPanel
            key={menu.key}
            items={menu.items}
            x={menu.x}
            y={menu.y}
            onClose={close}
          />
        </div>
      )}
    </MenuContext.Provider>
  );
}

export const separator = { type: "separator" };
